import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { packagesService } from '../services/packagesService';
import { 
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CalendarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const PackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const [packageData, setPackageData] = useState(null);
  const [dateSlots, setDateSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      loadPackageData();
    }
  }, [id]);

  const loadPackageData = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const [packageResult, slotsResult] = await Promise.all([
        packagesService.getPackage(id),
        packagesService.getDateSlots(id)
      ]);
      
      setPackageData(packageResult);
      setDateSlots(slotsResult);
    } catch (error) {
      console.error('Error loading package data:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load package data' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePackage = async () => {
    if (!packageData || !window.confirm('Are you sure you want to delete this package?')) {
      return;
    }

    try {
      setIsDeleting(true);
      await packagesService.deletePackage(packageData.id);
      dispatch({ type: 'DELETE_PACKAGE', payload: packageData.id });
      navigate('/packages');
    } catch (error) {
      console.error('Error deleting package:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete package' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteDateSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this date slot?')) {
      return;
    }

    try {
      await packagesService.deleteDateSlot(slotId);
      setDateSlots(prev => prev.filter(slot => slot.id !== slotId));
    } catch (error) {
      console.error('Error deleting date slot:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete date slot' });
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Package not found</h1>
          <p className="text-gray-600 mt-2">The package you're looking for doesn't exist.</p>
          <Link to="/packages" className="btn-primary mt-4">
            Back to Packages
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/packages')}
                className="mr-4 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{packageData.name}</h1>
                <p className="text-gray-600">Package details and date slots</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to={`/packages/${packageData.id}/edit`}
                className="btn-secondary flex items-center"
              >
                <PencilIcon className="h-5 w-5 mr-2" />
                Edit
              </Link>
              <button
                onClick={handleDeletePackage}
                disabled={isDeleting}
                className="btn-danger flex items-center disabled:opacity-50"
              >
                {isDeleting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <TrashIcon className="h-5 w-5 mr-2" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Package Info */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Package Information</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Description</h3>
                    <p className="mt-1 text-sm text-gray-900">{packageData.description}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Price</h3>
                    <p className="mt-1 text-2xl font-bold text-primary-600">
                      {formatPrice(packageData.price)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Created</h3>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDate(packageData.createdAt)}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDate(packageData.updatedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Date Slots */}
          <div className="lg:col-span-1">
            <div className="card">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Date Slots</h2>
                <Link
                  to={`/packages/${packageData.id}/date-slots/new`}
                  className="text-primary-600 hover:text-primary-500"
                >
                  <PlusIcon className="h-5 w-5" />
                </Link>
              </div>
              <div className="p-6">
                {dateSlots.length > 0 ? (
                  <div className="space-y-3">
                    {dateSlots.map((slot) => (
                      <div key={slot.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {formatDate(slot.date)}
                            </p>
                            {slot.time && (
                              <p className="text-xs text-gray-500 flex items-center">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                {formatTime(slot.time)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            slot.isAvailable 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {slot.isAvailable ? 'Available' : 'Unavailable'}
                          </span>
                          <button
                            onClick={() => handleDeleteDateSlot(slot.id)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <CalendarIcon className="mx-auto h-8 w-8 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">No date slots yet</p>
                    <Link
                      to={`/packages/${packageData.id}/date-slots/new`}
                      className="mt-2 text-sm text-primary-600 hover:text-primary-500"
                    >
                      Add date slot
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PackageDetail;
